import React from 'react'

interface CardFactsStripProps {
  /** Chips in priority order — the first ones win when the row is short. */
  children: React.ReactNode
  className?: string
}

/**
 * The one-line footer of a horizontal listing card (search list, map popup):
 * promo badge, size / spots, demand — always ONE line, never a wrap.
 *
 * Why a footer: the search card's photo is 130px wide below 428px and the
 * canonical «50% 1er mes · 6+ meses» needs ~146px, so on the photo it broke
 * into a two-line rosa block (Jules, 2026-10-28), and «47 consultas en 30
 * días» wrapped there too. The footer spans the whole card.
 *
 * How it stays one line: the inner row is a wrapping flex box exactly one
 * chip tall with `overflow-hidden` and a large row gap, so a chip that does
 * not fit drops out WHOLE instead of wrapping or being cut mid-word. Order
 * the children by priority.
 */
export default function CardFactsStrip({
  children,
  className = ''
}: CardFactsStripProps) {
  return (
    <div
      className={`shrink-0 border-t border-ink-100/15 px-3 py-1 md:px-4 md:py-1.5 ${className}`}
    >
      <div className="flex h-5 flex-wrap items-center gap-x-1.5 gap-y-6 overflow-hidden">
        {children}
      </div>
    </div>
  )
}

/** A neutral fact chip sized to the strip (size, spots). */
export const FactChip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex shrink-0 items-center rounded-md bg-cream-300 px-1.5 py-0.5 text-[11px] leading-tight font-medium whitespace-nowrap text-ink-700 md:text-xs">
    {children}
  </span>
)

/** A quieter chip for a soft signal (recent inquiries). */
export const SignalChip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex shrink-0 items-center gap-1 px-0.5 text-[11px] leading-tight font-medium whitespace-nowrap text-ink-400 md:text-xs">
    <span aria-hidden className="size-1 rounded-full bg-ink-400" />
    {children}
  </span>
)
